import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { PaymentMethodNameEnum } from "@/enums"
import { useRefundSubscription } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { ErrorHelper } from "@/utils"
const refundDialogSchema = z.object({
  op1: z.boolean().refine((value) => value, {
    message: "Você precisa concordar com esta opção",
  }),
  op2: z.boolean().refine((value) => value, {
    message: "Você precisa concordar com esta opção",
  }),
  op3: z.boolean().refine((value) => value, {
    message: "Você precisa concordar com esta opção",
  }),
  reason: z
    .string()
    .min(1, { message: "O motivo é obrigatório" }),
})
type RefundDialogSchemaProps = z.infer<
  typeof refundDialogSchema
>
interface RefundDialogProps {
  id: string
  billingCycle: number
  open: boolean
  setOpen: (open: boolean) => void
  paymentMethod?: PaymentMethodNameEnum
}
export const RefundSubscriptionDialog = ({
  id,
  billingCycle,
  open,
  setOpen,
  paymentMethod,
}: RefundDialogProps) => {
  const { t } = useTranslation()

  const isCancelPaymentMethod = [
    PaymentMethodNameEnum.PIX,
    PaymentMethodNameEnum.BANK_SLIP,
    PaymentMethodNameEnum.DEBIT_CARD,
    PaymentMethodNameEnum.DIRECT_DEBIT,
    PaymentMethodNameEnum.BOLEPIX,
  ].includes(paymentMethod as PaymentMethodNameEnum)

  const actionLabel = isCancelPaymentMethod
    ? "Cancelar"
    : "Reembolsar"
  const actionLabelLower = isCancelPaymentMethod
    ? "cancelamento"
    : "reembolso"
  const defaultValues: RefundDialogSchemaProps = {
    op1: false,
    op2: false,
    op3: false,
    reason: "",
  }
  const form = useForm<RefundDialogSchemaProps>({
    resolver: zodResolver(refundDialogSchema),
    defaultValues,
  })
  const handleResetForm = () => {
    form.reset(defaultValues)
  }
  const { mutateAsync: refundSubscription } =
    useRefundSubscription(id, billingCycle)
  const handleSubmit = form.handleSubmit(async (data) => {
    await refundSubscription(
      { reason: data.reason, id, billingCycle },
      {
        onSuccess: () => {
          ErrorHelper.showSuccess(
            `Assinatura ${actionLabelLower} com sucesso`
          )
          setOpen(false)
          handleResetForm()
        },
        onError: () => {
          toast({
            title: t("subscriptions.refundError"),
            variant: "destructive",
          })
        },
      }
    )
  })
  const formIsPending = form.formState.isSubmitting
  return (
    <AlertDialog onOpenChange={setOpen} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-xl">
            {actionLabel} parcela
          </AlertDialogTitle>
          <Separator />
          <AlertDialogDescription>
            Atenção! Esta ação interfere diretamente no
            fluxo da assinatura e não poderá ser desfeita.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <Form {...form}>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="op1"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Eu entendo que vou interferir no
                            andamento da assinatura.
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="op2"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Eu entendo que esta ação não
                            poderá ser desfeita.
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="op3"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            Eu entendo as consequências
                            desta ação no fluxo da
                            assinatura.
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder={`Motivo do ${actionLabelLower}`}
                          className="resize-none text-sm"
                          rows={5}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <Separator />
              <AlertDialogFooter>
                <AlertDialogCancel
                  disabled={formIsPending}
                  onClick={handleResetForm}
                >
                  Voltar
                </AlertDialogCancel>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={formIsPending}
                >
                  Confirmar
                </Button>
              </AlertDialogFooter>
            </form>
          </Form>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
